import { HttpClient } from '@angular/common/http'
import { inject, InjectionToken } from '@angular/core'
import { BehaviorSubject, map, merge, Observable, of, shareReplay, Subscription, switchMap, tap } from 'rxjs'
import { fetchUntilExhausted } from 'src/app/shared/utilities/fetch-until-exhausted'
import { sortBy } from 'lodash'
import { MDMAUnifiedStatus } from '../settlements/mdma-settlements.types'
import { EntityRefWithUID, EntityRef, ChargeMethod } from 'src/app/shared/models/referenceData'
import { SortParams } from '../../shared/utilities/http-params'

export type SearchFn<T> = (item: T, term?: string) => boolean

const searchEntity: SearchFn<EntityRef> = (item, term) => {
  const searchKeys: (keyof EntityRef)[] = ['entity_code', 'entity_name'] as const
  return term 
    ? searchKeys.some(k => item[k].toLowerCase().startsWith(term.toLowerCase()))
    : true
}

const entitySort: SortParams[] = [{ name: 'entity_name' }]

export abstract class BaseReferenceService<T> {
  protected constructor(protected readonly data$: Observable<T[]>, protected searchFn?: SearchFn<T>) {}

  get(search?: string): Observable<T[]> {
    if (search && this.searchFn) {
      return this.data$.pipe(
        map(data =>
          data.filter(entity => this.searchFn!(entity, search)),
        ),
      )
    } else {
      return this.data$
    }
  }

  abstract refresh(): void
  abstract init(): void
}

export class HardcodedReferenceService<T> extends BaseReferenceService<T> {
  constructor(values: T[], searchFn?: SearchFn<T>) {
    super(of(values), searchFn)
  }

  override refresh() { /* noop */ }
  override init() { /* noop */ }
}

const getFromStorage = <T> (storage: Storage, url: string): T[] | undefined => {
  const storageValue = storage.getItem(url)
  if (storageValue) {
    return JSON.parse(storageValue) as T[]
  } else {
    return undefined
  }
}

export class CachedReferenceService<T> extends BaseReferenceService<T> {
  protected refreshTrigger: BehaviorSubject<number>
  protected sub: Subscription

  constructor(http: HttpClient, storage: Storage, url: string, searchFn?: SearchFn<T>, chunkSize = 1000, sort: SortParams[] = []) {
    const [refreshTrigger, data] = CachedReferenceService.getData<T>(http, storage, url, chunkSize, sort)
    super(data, searchFn)
    this.refreshTrigger = refreshTrigger
  }

  protected static getData = <T> (
    http: HttpClient,
    storage: Storage,
    url: string,
    chunkSize: number,
    sort: SortParams[],
  ): [BehaviorSubject<number>, Observable<T[]>] => {
    const refreshTrigger$ = new BehaviorSubject(1)
    const storage$ = of(getFromStorage<T>(storage, url) ?? [])
    const server$ =  refreshTrigger$.pipe(
      switchMap(() => 
        new Observable<T[]>(subscriber => {
          fetchUntilExhausted<T>(http, url, chunkSize, 0, sort).then(({ results }) => {
            subscriber.next(results)
          })
        }),
      ),
      tap(results => {
        storage.setItem(url, JSON.stringify(results))
      }),
      shareReplay(1),
    )
    const data$ = merge(storage$, server$).pipe(shareReplay(1))
    return [refreshTrigger$, data$]
  }

  override init() {
    if(!this.sub) {
      this.sub = this.data$.subscribe()
    }
  }

  override refresh(): void {
    this.refreshTrigger.next(this.refreshTrigger.value + 1)
  }
}

export const BROWSER_STORAGE = new InjectionToken<Storage>(
  'Browser Storage',
  {
    providedIn: 'root',
    factory: () => sessionStorage,
  },
)

export const ENTITY_SEARCH = new InjectionToken<SearchFn<EntityRef>>(
  'Entity Search',
  {
    providedIn: 'root',
    factory: () => searchEntity,
  },
)

export const ENTITY_WITH_UID_SEARCH = new InjectionToken<SearchFn<EntityRefWithUID>>(
  'Entity Eith UID Search',
  {
    providedIn: 'root',
    factory: () => searchEntity,
  },
)

export const CHARGE_METHOD_SEARCH = new InjectionToken<SearchFn<ChargeMethod>>(
  'Entity Search',
  {
    providedIn: 'root',
    factory: () => searchEntity,
  },
)

const CACHED_COMPANIES = new InjectionToken<CachedReferenceService<EntityRef>>(
  'company',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/company',
      inject(ENTITY_SEARCH), 
      undefined,
      entitySort,
    ),
  },
)

const CACHED_CHANNELS = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'channel',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/channel',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_CONTROL_AREAS = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'control_area',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/control_area',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_CP_NODES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'cp_node',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/cp_node',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_DELIVERY_CLASSES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'delivery_class',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/delivery_class',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_INTERVAL_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'interval_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/interval_type',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_LOCAL_DISTRIBUTION_COMPANIES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'local_distribution_company',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/local_distribution_company',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_LOCATIONS = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'location',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/location',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_LOSS_CLASSES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'loss_class',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/loss_class',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_MEASUREMENT_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'measurement_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/measurement_type',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_MEASUREMENT_VALUE_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'measurement_value_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/measurement_value_type',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_METER_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'meter_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/meter_type',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_PROFILE_CLASSES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'profile_class',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/profile_class',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_RETAILERS = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'retailer',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/retailer',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_RUN_STATUSES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'run_status',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/run_status',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_SERVICE_POINT_SOURCES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'service_point_source',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/service_point_source',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_SETTLEMENT_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'settlement_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/settlement_type',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_MDMA_SETTLEMENT_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'mdma_settlement_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/settlement_type/mdma',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_TBILL_SETTLEMENT_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'tbill_settlement_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/settlement_type/tbill',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_BA_LOSS_SETTLEMENT_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'ba_loss_settlement_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient),
      inject(BROWSER_STORAGE),
      'reference/settlement_type/ba_loss',
      inject(ENTITY_WITH_UID_SEARCH),
    ),
  },
)

const CACHED_TBILL_SP_CHARGE_TYPES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'tbill_sp_charge_type',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/tbill_sp_charge_type',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const CACHED_TBILL_SP_CHARGE_METHODS = new InjectionToken<CachedReferenceService<ChargeMethod>>(
  'tbill_sp_charge_method',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/tbill_sp_charge_method',
      inject(CHARGE_METHOD_SEARCH), 
    ),
  },
)

const CACHED_SUPPLY_CLASSES = new InjectionToken<CachedReferenceService<EntityRefWithUID>>(
  'supply_class',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'reference/supply_class',
      inject(ENTITY_WITH_UID_SEARCH),
      undefined,
      entitySort, 
    ),
  },
)

const HARDCODED_MDMA_UNIFIED_STATUSES = new InjectionToken<HardcodedReferenceService<EntityRef>>(
  'mdma_unified_statuses',
  {
    providedIn: 'root',
    factory: () => new HardcodedReferenceService<EntityRef>(    
      sortBy(Object.entries(MDMAUnifiedStatus).map(([k, v]) => ({
        entity_code: k,
        entity_name: v,
      })), _ => _.entity_name),
      inject(ENTITY_SEARCH),
    ),
  },
)

const SCHEDULED_EVENT_CATEGORY = new InjectionToken<HardcodedReferenceService<EntityRef>>(
  'scheduled_event_category',
  {
    providedIn: 'root',
    factory: () => new HardcodedReferenceService<EntityRef>(
      [
        { entity_code: 'mdma_aggregation', entity_name: 'MDMA' }, 
        { entity_code: 'tbill_aggregation', entity_name: 'TBILL' },
      ],
      inject(ENTITY_SEARCH),
    ),
  },
)

export const referenceServiceTokens = {
  companies: CACHED_COMPANIES,
  channels: CACHED_CHANNELS,
  controlAreas: CACHED_CONTROL_AREAS,
  cpNodes: CACHED_CP_NODES,
  deliveryClasses: CACHED_DELIVERY_CLASSES,
  intervalTypes: CACHED_INTERVAL_TYPES,
  localDistributionCompanies: CACHED_LOCAL_DISTRIBUTION_COMPANIES,
  locations: CACHED_LOCATIONS,
  lossClasses: CACHED_LOSS_CLASSES,
  mdmaUnifiedStatuses: HARDCODED_MDMA_UNIFIED_STATUSES,
  measurementTypes: CACHED_MEASUREMENT_TYPES,
  measurementValueTypes: CACHED_MEASUREMENT_VALUE_TYPES,
  meterTypes: CACHED_METER_TYPES,
  profileClasses: CACHED_PROFILE_CLASSES,
  retailers: CACHED_RETAILERS,
  runStatuses: CACHED_RUN_STATUSES,
  servicePointSources: CACHED_SERVICE_POINT_SOURCES,
  settlementTypes: CACHED_SETTLEMENT_TYPES,
  mdmaSettlementTypes: CACHED_MDMA_SETTLEMENT_TYPES,
  tbillSettlementTypes: CACHED_TBILL_SETTLEMENT_TYPES,
  baLossSettlementTypes: CACHED_BA_LOSS_SETTLEMENT_TYPES,
  tbillSPChargeTypes: CACHED_TBILL_SP_CHARGE_TYPES,
  tbillSPChargeMethods: CACHED_TBILL_SP_CHARGE_METHODS,
  supplyClasses: CACHED_SUPPLY_CLASSES,
  scheduledEventCategory: SCHEDULED_EVENT_CATEGORY,
}

export type ReferenceDataSource = keyof typeof referenceServiceTokens
